import { PROMO_SECTIONS, parsePromoText } from '@/lib/promos';
import { datesInRange, slugify } from '@/lib/dates';

/* ── Formato do arquivo exportado ────────────────────────────────────
 * Contrato do plugin da arte ("Carregar semana"): `dias` tem de 1 a 7
 * itens, um por dia da semana, e todo item precisa de `evento` + `djs`
 * (1 a 4). Campo que não tem valor sai do JSON em vez de ir vazio.
 * ------------------------------------------------------------------ */

/** Máximo de dias por arquivo — um por dia da semana. */
export const MAX_DIAS_EXPORT = 7;
/** Máximo de DJs em `djs[]`; o excedente fica só no `djsTexto`. */
export const MAX_DJS_EXPORT = 4;

export interface ExportDj {
  id: string;
  nome: string;
}

export interface ExportPromoItem {
  item: string;
  preco: string;
}

export interface ExportPromoBloco {
  titulo: string;
  itens: ExportPromoItem[];
}

export interface DiaExport {
  data: string;
  evento: string;
  couvert?: string;
  /** Horário de abertura da casa, `HH:MM` — camada própria da arte, não é o couvert. */
  abertura?: string;
  djsTexto?: string;
  djs: ExportDj[];
  promos?: ExportPromoBloco[];
}

export interface ProgramacaoExport {
  periodo: { inicio: string; fim: string };
  dias: DiaExport[];
}

/** Só o que o export precisa de um evento — mantém o builder testável sem o tipo cheio da API. */
export interface ExportableEvent {
  name: string;
  event_date: string;
  open_time?: string | null;
  status?: string;
  couvert_type: string;
  couvert_value?: number | string | null;
  couvert_until?: string | null;
  promos?: string | null;
  attractions?: Array<{ name: string }> | null;
}

/* ── Helpers ─────────────────────────────────────────────────────── */

/**
 * "22:00" → "22H"; "22:30" → "22H30". O plugin da arte só reconhece hora como
 * número colado em `H` — "22:30" não é lido como horário.
 */
function horaCurta(time: string): string {
  const [h = '', m = ''] = time.slice(0, 5).split(':');
  return m === '00' ? `${h}H` : `${h}H${m}`;
}

/** `open_time` → `"HH:MM"`; `null`/formato inesperado → `null` (o campo sai do JSON). */
function abertura(time: string | null | undefined): string | null {
  const hhmm = (time ?? '').slice(0, 5);
  return /^\d{2}:\d{2}$/.test(hhmm) ? hhmm : null;
}

function valorBR(value: number): string {
  return Number.isInteger(value) ? `R$ ${value}` : `R$ ${value.toFixed(2).replace('.', ',')}`;
}

/**
 * Couvert como frase em caixa alta. O plugin não usa o texto como veio: reconhece
 * por palavra-chave qual dos três formatos da casa é (hora colada no `H`, valor em
 * `R$`, "FREE" só quando há período grátis) e reescreve na frase padrão da arte.
 * Separado de `fmtCouvert`/`fmtCouvertParts`, que servem à UI do app.
 */
export function fmtCouvertExport(
  type: string,
  value: number | string | null | undefined,
  until: string | null | undefined,
): string {
  const num = value == null || value === '' ? null : Number(value);
  const valorOk = num != null && Number.isFinite(num);

  if (type === 'free') return 'FREE A NOITE TODA';

  if (type === 'free_until') {
    if (!until) return 'FREE A NOITE TODA';
    const hora = horaCurta(until);
    const base = `FREE ATÉ ÀS ${hora}`;
    return valorOk ? `${base} · APÓS ${hora} VALOR: ${valorBR(num)}` : base;
  }

  if (type === 'flat' && valorOk) return `${valorBR(num)} NA PORTA`;
  return '';
}

/** "DJ Luar" → "djluar" (é o nome do arquivo de foto na biblioteca do plugin). */
export function djId(nome: string): string {
  return slugify(nome).replace(/-/g, '');
}

function promosDoEvento(raw: string | null | undefined): ExportPromoBloco[] {
  if (!raw) return [];
  const sections = parsePromoText(raw);
  return PROMO_SECTIONS.map((def) => ({
    titulo: def.label,
    itens: sections[def.key]
      .filter((l) => l.name.trim() && l.price.trim())
      .map((l) => ({ item: `${def.prefix}${l.name.trim()}`, preco: l.price.trim() })),
  })).filter((bloco) => bloco.itens.length > 0);
}

function djsDoEvento(event: ExportableEvent): ExportDj[] {
  return (event.attractions ?? [])
    .map((a) => a.name?.trim() ?? '')
    .filter(Boolean)
    .map((nome) => ({ id: djId(nome), nome }));
}

/* ── Builder ─────────────────────────────────────────────────────── */

export interface BuildResult {
  data: ProgramacaoExport;
  /** Datas em que havia mais de um evento publicado — só o primeiro entrou no arquivo. */
  diasComVariosEventos: string[];
  /** Datas deixadas de fora por não terem DJ no line-up (`djs` é obrigatório). */
  diasSemDj: string[];
}

/**
 * Monta o JSON da programação: um item por dia **com evento publicado**.
 * Dia sem evento não entra na lista (o plugin exige `evento` + `djs` em todo item);
 * rascunho e cancelado não contam como publicado.
 */
export function buildProgramacaoExport(
  events: ExportableEvent[],
  periodo: { inicio: string; fim: string },
): BuildResult {
  const porData = new Map<string, ExportableEvent[]>();
  for (const e of events) {
    if (e.status && e.status !== 'published') continue;
    const key = e.event_date.slice(0, 10);
    const arr = porData.get(key) ?? [];
    arr.push(e);
    porData.set(key, arr);
  }

  const diasComVariosEventos: string[] = [];
  const diasSemDj: string[] = [];
  const dias: DiaExport[] = [];

  for (const data of datesInRange(periodo.inicio, periodo.fim)) {
    const doDia = (porData.get(data) ?? []).sort((a, b) =>
      (a.open_time ?? '').localeCompare(b.open_time ?? ''),
    );
    const event = doDia[0];
    if (!event) continue;
    if (doDia.length > 1) diasComVariosEventos.push(data);

    const todosDjs = djsDoEvento(event);
    if (todosDjs.length === 0) {
      diasSemDj.push(data);
      continue;
    }

    const couvert = fmtCouvertExport(event.couvert_type, event.couvert_value, event.couvert_until);
    const abre = abertura(event.open_time);
    const promos = promosDoEvento(event.promos);

    // Ordem das chaves = ordem do contrato do plugin (JSON.stringify preserva).
    dias.push({
      data,
      evento: event.name,
      ...(couvert ? { couvert } : {}),
      ...(abre ? { abertura: abre } : {}),
      djsTexto: todosDjs.map((d) => d.nome.toUpperCase()).join(' + '),
      djs: todosDjs.slice(0, MAX_DJS_EXPORT),
      ...(promos.length > 0 ? { promos } : {}),
    });
  }

  return {
    data: { periodo: { inicio: periodo.inicio, fim: periodo.fim }, dias },
    diasComVariosEventos,
    diasSemDj,
  };
}

/** `programacao-2026-07-24_2026-07-26.json` */
export function exportFileName(periodo: { inicio: string; fim: string }): string {
  return `programacao-${periodo.inicio}_${periodo.fim}.json`;
}
